import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardKpiCard,
  type DefinitionTooltipInfo,
} from "src/components/common";
import type { GoogleKpiCard, GoogleKpiTooltipKey } from "./GoogleAdsTableConfig";

const SparklineKpiCard: FC<{
  title: string;
  value: string;
  icon: ReactNode;
  tooltipAlign?: "start" | "end";
  tooltip: DefinitionTooltipInfo;
  comparisonLabel: string;
  noTrendLabel: string;
  noTrendTooltip: string;
  formulaTitle: string;
  changePercent?: number | null;
  changePercentFormattedValue?: string;
  trendPolarity?: "higher-is-better" | "lower-is-better";
}> = ({
  title,
  value,
  icon,
  tooltipAlign = "start",
  tooltip,
  comparisonLabel,
  noTrendLabel,
  noTrendTooltip,
  formulaTitle,
  changePercent,
  changePercentFormattedValue,
  trendPolarity,
}) => {
  return (
    <DashboardKpiCard
      className="paid-media-kpi-card"
      title={title}
      value={value}
      icon={icon}
      changePercent={changePercent}
      changePercentFormattedValue={changePercentFormattedValue}
      changePercentTreatZeroAsMissing={false}
      hideNoTrendFallback
      comparisonLabel={comparisonLabel}
      noTrendLabel={noTrendLabel}
      noTrendTooltip={noTrendTooltip}
      formulaTitle={formulaTitle}
      tooltip={tooltip}
      tooltipAlign={tooltipAlign}
      metricLabelForTooltip={title}
      trendPolarity={trendPolarity}
    />
  );
};

interface GoogleKpiGridProps {
  kpis: GoogleKpiCard[];
  kpiIcons: ReactNode[];
  getGoogleKpiTooltip: (_key: GoogleKpiTooltipKey) => DefinitionTooltipInfo;
}

const GoogleKpiGrid: FC<GoogleKpiGridProps> = ({ kpis, kpiIcons, getGoogleKpiTooltip }) => {
  const { t } = useTranslation();

  return (
    <div className="paid-media-kpi-grid google-ads-kpi-grid">
      {kpis.map((card, index) => (
        <SparklineKpiCard
          key={card.liveKey}
          title={card.title}
          value={card.value}
          icon={kpiIcons[index]}
          changePercent={card.change}
          changePercentFormattedValue={card.changeFormattedValue}
          comparisonLabel={t("googleAdsReport.vsLastPeriod")}
          noTrendLabel={t("webAnalytics.kpi.trendUnavailable")}
          noTrendTooltip={t("webAnalytics.kpi.noEarlierData")}
          formulaTitle={t("webAnalytics.kpi.formula")}
          tooltip={getGoogleKpiTooltip(card.tooltipKey)}
          tooltipAlign={index % 5 >= 3 ? "end" : "start"}
          trendPolarity={card.polarity}
        />
      ))}
    </div>
  );
};

export default GoogleKpiGrid;
